import React from 'react';

import { TableCell, TableBody, TableRow } from '@gogame/components';
import { useDateFormat } from '@gogame/intl-ts';

import {
  AFFECTED_LEVEL,
  SECONDARY_AFFECTED_LEVEL,
} from 'scenes/SmartRules/SmartRuleForm/General';
import {
  useFormatValue,
  useIsReversedCompareColumns,
  PERCENT,
} from 'scenes/SmartRules/hooks';

import InfoRow from './InfoRow';
import {
  FlexContainer,
  NoBorderCell,
  TableCellStatus,
  ValueContainer,
} from './styled';

const INITIAL_ROW_SPAN_SIZE_WITH_DATA = 2;
const INITIAL_ROW_SPAN_SIZE = 1;
const getRow = ({
  withBorder,
  isCompared,
  compareValue,
  value = '-',
  diffValue,
  isSuccess,
  key,
}) => {
  const row = [];
  const Cell = withBorder ? TableCell : NoBorderCell;

  if (isCompared) {
    row.push(
      <Cell align={'right'} key={key}>
        <FlexContainer>
          <ValueContainer>{compareValue}</ValueContainer>
        </FlexContainer>
      </Cell>
    );
  }
  row.push(
    <Cell align={'right'} key={key + 1}>
      <FlexContainer>
        <ValueContainer>{value}</ValueContainer>
      </FlexContainer>
    </Cell>
  );
  if (isCompared) {
    row.push(
      <TableCellStatus
        isSuccess={isSuccess}
        align={'right'}
        noBottomBorder={!withBorder}
        key={key + 2}
      >
        {diffValue}
      </TableCellStatus>
    );
  }

  return row;
};

const getCell = (withBorder, text) => {
  return withBorder ? (
    <TableCell width={'150px'}>{text}</TableCell>
  ) : (
    <NoBorderCell width={'150px'}>{text}</NoBorderCell>
  );
};
const AffectedEntitiesBody = ({
  affectedEntities,
  actionMetrics,
  metrics,
  ruleLogId,
}) => {
  const formatValue = useFormatValue();
  const dateFormat = useDateFormat({ template: 'LLL', timeZone: 'local' });
  const isReversedCompareColumns = useIsReversedCompareColumns();
  return (
    <React.Fragment>
      {affectedEntities.map(
        (
          {
            actionDimension,
            [AFFECTED_LEVEL]: affectedLevel,
            [SECONDARY_AFFECTED_LEVEL]: secondaryAffectedLevel,
            affiliateName,
            campaignName,
            data,
            historyData,
            thumbnailUrl,
          },
          i
        ) => {
          const labelName = [affiliateName, campaignName]
            .filter((i) => i)
            .join(' / ');
          const actionMetricsValues = actionMetrics.map(
            ({ id, metricValueType }) => ({
              metricValueType,
              ...data[id],
            })
          );
          const metricValues = metrics.map(
            ({ id, metricValueType, isCompared }) => ({
              isCompared,
              metricValueType,
              ...data[id],
              historyData: historyData?.[id] || [],
              id,
            })
          );
          const historyDataSize = historyData?.[metrics[0]?.id]?.length || 0;
          const isDataRow = data?.[metrics[0]?.id] || historyDataSize;
          const totalRowSpan =
            historyDataSize +
            (isDataRow
              ? INITIAL_ROW_SPAN_SIZE_WITH_DATA
              : INITIAL_ROW_SPAN_SIZE);

          return (
            <TableBody key={'row' + i}>
              <InfoRow
                secondaryAffectedLevel={secondaryAffectedLevel}
                totalRowSpan={totalRowSpan}
                affectedLevelName={affectedLevel?.name}
                thumbnailUrl={thumbnailUrl}
                labelName={labelName}
                actionDimensionName={actionDimension?.name}
                ruleLogId={ruleLogId}
                affectedLevelId={affectedLevel?.id}
                actionMetricsValues={actionMetricsValues}
              />

              {metricValues?.reduce(
                (
                  prev,
                  {
                    historyData,
                    value,
                    compareValue,
                    diff,
                    isCompared,
                    metricValueType,
                    id,
                  },
                  index
                ) => {
                  const valueRow = prev[0]
                    ? [...prev[0]]
                    : [getCell(!historyData.length, '-')];

                  const reversedCompareColumns = isReversedCompareColumns(id);
                  valueRow.push(
                    getRow({
                      withBorder: !historyData.length,
                      isCompared,
                      compareValue: formatValue(compareValue, metricValueType),
                      compareCellWithAlign: true,
                      value: formatValue(value, metricValueType),
                      diffValue: formatValue(diff, PERCENT),
                      isSuccess: reversedCompareColumns
                        ? value < compareValue
                        : value > compareValue,
                      key: index,
                    })
                  );
                  prev[0] = valueRow;

                  historyData.forEach(
                    (
                      { metricData: { value, compareValue, diff } = {}, date },
                      i
                    ) => {
                      const row = prev[i + 1]
                        ? [...prev[i + 1]]
                        : [
                            getCell(
                              i + 1 === historyData.length,
                              dateFormat(date)
                            ),
                          ];

                      row.push(
                        getRow({
                          withBorder: i + 1 === historyData.length,
                          isCompared,
                          compareValue: formatValue(
                            compareValue,
                            metricValueType
                          ),
                          value: formatValue(value, metricValueType),
                          diffValue: formatValue(diff, PERCENT),
                          isSuccess: reversedCompareColumns
                            ? value < compareValue
                            : value > compareValue,
                          key: index * 10000 + i,
                        })
                      );

                      prev[i + 1] = row;
                    }
                  );

                  if (index + 1 === metricValues.length) {
                    prev = prev.map((_, index_) => (
                      <TableRow key={'sub' + i + '_' + index_}>
                        {prev[index_]}
                      </TableRow>
                    ));
                  }
                  return prev;
                },
                []
              )}
            </TableBody>
          );
        }
      )}
    </React.Fragment>
  );
};

export default AffectedEntitiesBody;
